import { Box, Divider, Stack, Typography } from '@mui/material'
import { useTheme, styled } from '@mui/material/styles'
import { KCheckIconButton } from 'components/core/KCheckIconButton'
import {
  AmericanExpressIcon,
  ApplePayIcon,
  CreditCardIcon,
  DiscoverIcon,
  MasterCardIcon,
  VisaIcon,
} from 'components/images'
import { useCurrentStore, useOrdering } from 'hooks'
import { PaymentMethod } from 'models'
import { Fragment, useMemo } from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { SubmitOrderSectionProps } from './SubmitOrderSectionProps'

const PaymentOptionRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  gap: theme.spacing(4),
  '&:hover': {
    backgroundColor: theme.palette.grey['100'],
  },
}))

interface PaymentOptionProps {
  onClick: (paymentMethod: PaymentMethod) => void
  paymentMethod: PaymentMethod
  checked?: boolean
}

const PaymentOption = ({
  onClick,
  paymentMethod,
  checked,
}: PaymentOptionProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  return paymentMethod === 'credit_card' ? (
    <PaymentOptionRow onClick={() => onClick('credit_card')}>
      <CreditCardIcon />
      <Typography variant="h5">{t('Credit card')}</Typography>
      <Stack
        direction="row"
        spacing={1}
        sx={{ [theme.breakpoints.down('md')]: { display: 'none' } }}
      >
        <VisaIcon />
        <MasterCardIcon />
        <AmericanExpressIcon />
        <DiscoverIcon />
      </Stack>
      <Box sx={{ flexGrow: 1 }} />
      <KCheckIconButton checked={checked} />
    </PaymentOptionRow>
  ) : (
    <PaymentOptionRow onClick={() => onClick('apple_pay')}>
      <ApplePayIcon />
      <Typography variant="h5">{t('Apple pay')}</Typography>
      <Box sx={{ flexGrow: 1 }} />
      <KCheckIconButton checked={checked} />
    </PaymentOptionRow>
  )
}

export const OrderPaymentOptions = ({
  form: { control, register },
}: SubmitOrderSectionProps) => {
  const theme = useTheme()

  const { store } = useCurrentStore()
  const {  saveOrderProps } = useOrdering()

  const paymentMethods = useMemo(() => {
    const paymentMethods: PaymentMethod[] = ['credit_card']
    if (store?.accept_apple_pay === true) {
      paymentMethods.push('apple_pay')
    }
    return paymentMethods
  }, [store])

  if (!register) {
    return null
  }

  return (
    <Box
      sx={{
        borderRadius: 4,
        border: '1px solid',
        borderColor: theme.palette.grey['400'],
        overflow: 'hidden',
      }}
    >
      <Controller
        name="paymentInfo.paymentMethod"
        control={control}
        defaultValue={paymentMethods[0]}
        render={({ field }) => (
          <>
            {paymentMethods.map((paymentMethod, index) => (
              <Fragment key={paymentMethod}>
                <PaymentOption
                  onClick={(value) => {
                    field.onChange({ target: { value } })
                    saveOrderProps({payment_method: value})
                  }}
                  paymentMethod={paymentMethod}
                  checked={field.value === paymentMethod}
                />
                {index !== paymentMethods.length - 1 && <Divider />}
              </Fragment>
            ))}
          </>
        )}
      />
    </Box>
  )
}
